import { ECookiesStorageKeys } from 'common/enums/cookies-storage.enums.ts';
import { IOrderFormData } from 'domains/transaction/interfaces/order-form.interfaces.ts';

export const getCookieUtil = (name: string): string | null => {
  const cookieArr = document.cookie.split(';');
  let data: string | null = null;

  cookieArr.some((cookie) => {
    const cookiePair = cookie.trim().split('=');

    if (cookiePair[0] === name) {
      data = decodeURIComponent(cookiePair[1]);

      return true;
    }

    return false;
  });

  return data;
};

export const setCookieUtil = (name: ECookiesStorageKeys, encodedData: string, expires: string): void => {
  const { host } = window.location;
  const hostArr = host.split('.').reverse();
  const domain = `${hostArr[1]}.${hostArr[0]}`;
  document.cookie = `${name}=${encodedData}; expires=${expires}; path=/; domain=${domain}`;
};

export const deleteCookieUtil = (name: ECookiesStorageKeys): void => {
  const { host } = window.location;
  const hostArr = host.split('.').reverse();
  const domain = `${hostArr[1]}.${hostArr[0]}`;
  const expires = 'Thu, 01 Jan 1970 00:00:00 UTC';
  document.cookie = `${name}=; expires=${expires}; path=/; domain=${domain};`;
};

const cookieUtils = {
  setCookie: (name: string, value: string): void => {
    if (value) {
      const expires = 'Fri, 31 Dec 9999 23:59:59 GMT';
      setCookieUtil(name as ECookiesStorageKeys, value, expires);
    }
  },
  getCookie: (name: string): string | null => {
    const dataFromCookie = getCookieUtil(name);

    if (dataFromCookie) {
      return dataFromCookie;
    }

    return null;
  },

  removeCookie: (name: string): void => {
    deleteCookieUtil(name as ECookiesStorageKeys);
  }
};

export const cookieStorageUtil = <T>(): {
  getItem: (name: string, initialValue: T) => T;
  setItem: (name: string, newValue: T) => void;
  removeItem: (name: string) => void;
} => ({
  getItem: (key: string, initialValue: T): T => {
    const value = cookieUtils.getCookie(key) as T | null;
    return value ?? initialValue;
  },
  setItem: (key: string, newValue: T):void => {
    cookieUtils.setCookie(key, newValue as string);
  },
  removeItem: (key: string): void => {
    cookieUtils.removeCookie(key);
  },
});

export const setOrderFormDataToCookie = (data: Partial<IOrderFormData>): void => {
  const expires = 'Fri, 31 Dec 9999 23:59:59 GMT';
  const prevCookieData = getCookieUtil(ECookiesStorageKeys.OrderFormData);
  const decodedCookieData = prevCookieData ? (JSON.parse(prevCookieData) as IOrderFormData) : {};
  const mergedData = { ...decodedCookieData, ...data };
  const encodedData = JSON.stringify(mergedData);

  setCookieUtil(ECookiesStorageKeys.OrderFormData, encodedData, expires);
};

export const getOrderFormDataFromCookie = (): IOrderFormData | null => {
  try {
    const dataFromCookie = getCookieUtil(ECookiesStorageKeys.OrderFormData);

    if (dataFromCookie) {
      return JSON.parse(dataFromCookie) as IOrderFormData;
    }

    return null;
  } catch (error) {
    return null;
  }
};

export const setTransactionGuidToCookie = (guid: string): void => {
  const expires = 'Fri, 31 Dec 9999 23:59:59 GMT';
  setCookieUtil(ECookiesStorageKeys.TransactionGUID, guid, expires);
};

export const getTransactionGuidFromCookie = (): string => {
  try {
    const dataFromCookie = getCookieUtil(ECookiesStorageKeys.TransactionGUID);

    if (dataFromCookie) {
      return dataFromCookie;
    }

    return '';
  } catch (error) {
    return '';
  }
};

export const deleteTransactionGuidFromCookie = (): void => {
  deleteCookieUtil(ECookiesStorageKeys.TransactionGUID);
};

export const deleteTransactionSignatureFromCookie = (): void => {
  deleteCookieUtil(ECookiesStorageKeys.TransactionSignatureData);
};

export const setUserEmailToCookie = (email: string): void => {
  const expires = 'Fri, 31 Dec 9999 23:59:59 GMT';
  setCookieUtil(ECookiesStorageKeys.UserEmail, email, expires);
};

export const getUserEmailFromCookie = (): string => {
  try {
    const dataFromCookie = getCookieUtil(ECookiesStorageKeys.UserEmail);

    if (dataFromCookie) {
      return dataFromCookie;
    }

    return '';
  } catch (error) {
    return '';
  }
};

export const setCountryResidenceToCookie = (country: string): void => {
  const expires = 'Fri, 31 Dec 9999 23:59:59 GMT';
  setCookieUtil(ECookiesStorageKeys.CountryResidence, country, expires);
};

export const getCountryResidenceFromCookie = (): string => {
  try {
    const dataFromCookie = getCookieUtil(ECookiesStorageKeys.CountryResidence);

    if (dataFromCookie) {
      return dataFromCookie;
    }

    return '';
  } catch (error) {
    return '';
  }
};

export const setTransactionFiltersDataToCookie = (filters: string): void => {
  const expires = 'Fri, 31 Dec 9999 23:59:59 GMT';
  setCookieUtil(ECookiesStorageKeys.TransactionFilters, filters, expires);
};

export const getTransactionFiltersDataFromCookie = (): string => {
  try {
    const dataFromCookie = getCookieUtil(ECookiesStorageKeys.TransactionFilters);

    if (dataFromCookie) {
      return dataFromCookie;
    }

    return '';
  } catch (error) {
    return '';
  }
};

export const setFpfFinalStepRedirectToCookie = (path: string): void => {
  const { host } = window.location;
  const hostArr = host.split('.').reverse();
  const domain = `${hostArr[1]}.${hostArr[0]}`;
  document.cookie = `${ECookiesStorageKeys.FpfFinalStepRedirect}=${encodeURIComponent(path)}; path=/; domain=${domain}`;
};

export const getFpfFinalStepRedirectFromCookie = (): string => {
  try {
    const dataFromCookie = getCookieUtil(ECookiesStorageKeys.FpfFinalStepRedirect);
    return dataFromCookie ? decodeURIComponent(dataFromCookie) : '';
  } catch (error) {
    return '';
  }
};

export const deleteFpfFinalStepRedirectFromCookie = (): void => {
  deleteCookieUtil(ECookiesStorageKeys.FpfFinalStepRedirect);
};
